import _ from 'lodash';
import actionTypes from './tpaActionTypes';
import * as selectors from './tpaSelectors';
import * as selectionActions from '../selection/selectionActions';
import * as panelsActions from '../panels/panelsActions';
import * as platformEvents from 'platformEvents';
import * as util from '#packages/util';
//eslint-disable-next-line @wix/santa-editor/scoped-imports
import constants from '@/tpa/constants/constants'; // is circular import
import experiment from 'experiment';

import type { AppData, DSAction, DSRead } from 'types/documentServices';
import type { Dispatch, ThunkAction } from 'types/redux';
import type { EditorAPI } from '#packages/editorAPI';

const ASCEND_APP_DEF_ID = 'ee21fe60-48c5-45e9-95f4-6ca8f9b1c9d9';

const createAddAppDefIdsToRefreshOnDashboardClose = (appIds: AnyFixMe) => ({
  type: actionTypes.ADD_APP_IDS_TO_REFRESH_ON_DASHBOARD_CLOSE,
  appIds,
});

const createSetAppDefIdsToRefreshOnDashboardClose = (appIds: AnyFixMe) => ({
  type: actionTypes.SET_APP_IDS_TO_REFRESH_ON_DASHBOARD_CLOSE,
  appIds,
});

const createAddAppToTrackForUpgrade = (
  applicationId: number,
  applicationData: AppData,
) => ({
  type: actionTypes.ADD_APP_TO_TRACK_FOR_UPGRADE,
  applicationId,
  applicationData,
});

const createRemoveAppFromTrackForUpgrade = (applicationId: number) => ({
  type: actionTypes.REMOVE_APP_FROM_TRACK_FOR_UPGRADE,
  applicationId,
});

const shouldRefreshGeneralInfo = (refresh: AnyFixMe) => ({
  type: actionTypes.SHOULD_REFRESH_GENERAL_INFO,
  refresh,
});

const normalizeAppIds = (appIdsToNormalize: AnyFixMe) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _.map(appIdsToNormalize, normalizeAppId);
const normalizeAppId = (appIdToNormalize: AnyFixMe) =>
  parseInt(appIdToNormalize, 10);
const getAppIdsToRefresh = (appDefIds: string[], dsRead: DSRead) =>
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line you-dont-need-lodash-underscore/map
  _(appDefIds)
    .map(dsRead.tpa.app.getDataByAppDefId)
    .map('applicationId')
    .compact()
    .value();

const addAppDefIdsToRefreshOnDashboardClose =
  (appDefIds: string[]) =>
  (dispatch: Dispatch, getState: AnyFixMe, { dsRead }: { dsRead: DSRead }) => {
    const appIdsToRefresh = getAppIdsToRefresh(appDefIds, dsRead);
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    if (_.includes(appDefIds, constants.BUSINESS_MANAGER.GENERAL_INFO)) {
      dispatch(shouldRefreshGeneralInfo(true));
    }
    dispatch(
      createAddAppDefIdsToRefreshOnDashboardClose(
        normalizeAppIds(appIdsToRefresh),
      ),
    );
  };

const setAppDefIdsToRefreshOnDashboardClose =
  (appDefIds: string[]) =>
  (dispatch: Dispatch, getState: AnyFixMe, { dsRead }: { dsRead: DSRead }) => {
    const appIdsToRefresh = getAppIdsToRefresh(appDefIds, dsRead);
    dispatch(
      createSetAppDefIdsToRefreshOnDashboardClose(
        normalizeAppIds(appIdsToRefresh),
      ),
    );
  };

const addAppToTrackForUpgrade =
  (applicationId: number): ThunkAction =>
  (dispatch, _getState, { dsRead }: { dsRead: DSRead }) => {
    const appData = dsRead.tpa.app.getData(applicationId);
    const applicationData = {
      appDefinitionId: appData.appDefinitionId,
      vendorProductId: appData.vendorProductId,
      // @ts-expect-error
      upgradedToYearly: appData.upgradedToYearly === true,
      instance: appData.instance,
    } as unknown as AppData;

    dispatch(createAddAppToTrackForUpgrade(applicationId, applicationData));
  };

const removeAppFromTrackForUpgrade =
  (applicationId: number): ThunkAction =>
  (dispatch) => {
    dispatch(createRemoveAppFromTrackForUpgrade(applicationId));
  };

const isUpgradedToYearly = (tpaPremiumState: AnyFixMe) =>
  tpaPremiumState && tpaPremiumState.cycle === 6;

const openRefreshConfirmOnUpgradeAscend =
  (appDefIdToPremiumState: AnyFixMe, applicationId: number): ThunkAction =>
  (
    dispatch,
    _getState,
    { dsRead, editorAPI }: { dsRead: DSRead; editorAPI: EditorAPI },
  ) => {
    const appData = dsRead.tpa.app.getData(applicationId);
    // @ts-expect-error
    const parentAppDefId = appData?.appFields?.premiumBundle?.parentAppId;
    if (
      parentAppDefId === ASCEND_APP_DEF_ID &&
      _.isObject(appDefIdToPremiumState[parentAppDefId])
    ) {
      editorAPI.panelManager.openPanel(
        'tpaPanels.confirmation.refreshForUpgrade',
        {},
        false,
      );
      dispatch(removeAppFromTrackForUpgrade(applicationId));
    }
  };

const updateUpgradedApplications =
  (): ThunkAction =>
  (
    dispatch,
    getState,
    { dsRead, dsActions }: { dsRead: DSRead; dsActions: DSAction },
  ) => {
    const appsToTrack = selectors.getAppsToTrackForUpgrade(getState());

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/size
    if (_.size(appsToTrack) === 0) {
      return;
    }

    const metaSiteId = dsRead.generalInfo.getMetaSiteId();
    // @ts-expect-error
    dsRead.tpa.billing.getPremiumApps(metaSiteId, function (premiumApps) {
      const appDefIdToPremiumState = _.keyBy(premiumApps, 'appDefinitionId');
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line you-dont-need-lodash-underscore/for-each
      _.forEach(appsToTrack, (appToTrack, applicationId: number) => {
        const tpaPremiumState =
          appDefIdToPremiumState[appToTrack.appDefinitionId];
        if (_.isObject(tpaPremiumState)) {
          if (
            // @ts-expect-error
            appToTrack.vendorProductId !== tpaPremiumState.vendorProductId ||
            appToTrack.upgradedToYearly !== isUpgradedToYearly(tpaPremiumState)
          ) {
            dispatch(removeAppFromTrackForUpgrade(applicationId));
            const getRenderedReactCompsByApplicationId = experiment.isOpen(
              'se_appDefIdUpdates',
            )
              ? dsRead.tpaV2.app.getAllComps(appToTrack.appDefinitionId)
              : dsRead.tpa.app.getRenderedReactCompsByApplicationId(
                  applicationId,
                );

            const refreshAppCallback = () => {
              dispatch(selectionActions.clearSelectedComponents());
              dispatch(panelsActions.closeOpenedPanels());
              dsActions.tpa.app.refreshApp(
                getRenderedReactCompsByApplicationId,
              );

              dsActions.platform.notifyApplication(
                applicationId,
                platformEvents.factory.appUpgradeCompleted({
                  // @ts-expect-error
                  vendorProductId: tpaPremiumState.vendorProductId,
                  // @ts-expect-error
                  cycle: tpaPremiumState.cycle,
                  instance: appToTrack.instance,
                }),
              );
            };
            if (experiment.isOpen('se_appDefIdUpdates')) {
              dsActions.tpaV2.provision.refreshAppSpecMap(
                appToTrack.appDefinitionId,
                metaSiteId,
                refreshAppCallback,
              );
            } else {
              // @ts-expect-error
              dsActions.tpa.provision.refreshAppSpecMap(
                applicationId,
                metaSiteId,
                refreshAppCallback,
              );
            }
          }
        } else {
          dispatch(
            openRefreshConfirmOnUpgradeAscend(
              appDefIdToPremiumState,
              applicationId,
            ),
          );
        }
      });
    });
  };

const deleteApp =
  (appDefinitionId: string, options = {}, callback: AnyFixMe): ThunkAction =>
  (dispatch, getState, { editorAPI }: { editorAPI: EditorAPI }) => {
    const appData: AppData =
      editorAPI.dsRead.tpa.app.getDataByAppDefId(appDefinitionId);
    const applicationId = appData?.applicationId;
    const currentPageId = editorAPI.pages.getCurrentPage()?.id;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/filter
    const appPageIds = _(editorAPI.pages.getPagesData())
      .filter(
        (pageData) =>
          pageData.tpaApplicationId === applicationId ||
          pageData.managingAppDefId === appDefinitionId,
      )
      .map('id')
      .value();
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line you-dont-need-lodash-underscore/includes
    if (_.includes(appPageIds, currentPageId)) {
      editorAPI.pages.beforePagesRemoveHook(
        appPageIds,
        function () {
          if (experiment.isOpen('se_appDefIdUpdates')) {
            editorAPI.dsActions.tpaV2.pltform.uninstall(
              appData.appDefinitionId,
              callback,
              callback,
            );
          } else {
            editorAPI.dsActions.tpa.app.delete(
              // @ts-expect-error
              applicationId,
              callback,
              callback,
            );
          }
        },
        undefined,
      );
    } else {
      // @ts-expect-error
      editorAPI.dsActions.tpa.app.delete(applicationId, callback, callback);
    }
    util.editorWixRecorder.addLabel(
      `${appData.appDefinitionName} component removed`,
    );
    editorAPI.bi.reportBI(util.bi.events.COMPONENT_REMOVED, [
      {
        app_id: appDefinitionId,
        app_site_id: appData.instanceId,
        is_last_tpa: 1,
        // @ts-expect-error
        removal_method: options.removal_method,
      },
    ]);
  };

const setTpaSettingsPanelExtraProps = (props: AnyFixMe) => ({
  type: actionTypes.SET_TPA_SETTINGS_PANEL_EXTRA_PROPS,
  props,
});

export {
  addAppDefIdsToRefreshOnDashboardClose,
  setAppDefIdsToRefreshOnDashboardClose,
  setTpaSettingsPanelExtraProps,
  addAppToTrackForUpgrade,
  removeAppFromTrackForUpgrade,
  updateUpgradedApplications,
  deleteApp,
  shouldRefreshGeneralInfo,
};
